import React from 'react';
import PropTypes from 'prop-types';
import { cx, css } from 'emotion';
import ThemeContext from '@weave-design/theme-context';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function stylesheet(props, themeData) {
  function typographyStyle(variant, margin) {
    return {
      color: themeData[`typography.${variant}.color`],
      display: "block",
      fontFamily: themeData[`typography.${variant}.fontFamily`],
      fontSize: themeData[`typography.${variant}.fontSize`],
      fontWeight: themeData[`typography.${variant}.fontWeight`],
      lineHeight: themeData[`typography.${variant}.lineHeight`],
      margin: margin || 0,
      textAlign: "initial"
    };
  }

  const {
    stylesheet: customStylesheet
  } = props;
  const baseStyles = typographyStyle("body", "0");
  const listStyles = {
    "ul, ol": {
      paddingLeft: themeData["density.spacings.large"]
    },
    "ul li": {
      listStyle: "none",
      "&:before": {
        content: "'\\B7'",
        verticalAlign: "middle",
        fontSize: themeData["density.fontSizes.large"],
        paddingRight: themeData["density.spacings.small"],
        marginLeft: "-14px"
      }
    }
  };
  const headerStyles = {
    h1: typographyStyle("h1"),
    h2: typographyStyle("h2"),
    h3: typographyStyle("h3")
  };
  const anchorStyles = {
    a: {
      textDecoration: "none",
      cursor: "pointer",
      color: themeData["textLink.primary.default.fontColor"],
      outline: "none",
      "&:hover": {
        color: themeData["textLink.primary.hover.fontColor"],
        textDecoration: "underline",
        textDecorationColor: themeData["textLink.primary.hover.underlineColor"]
      },
      "&:focus": {
        color: themeData["textLink.primary.focus.fontColor"],
        outline: `solid ${themeData["textLink.focus.haloWidth"]} ${themeData["textLink.focus.haloColor"]}`
      }
    }
  };
  const boldStyles = {
    "b, strong": {
      fontWeight: themeData["basics.fontWeights.bold"]
    }
  };
  const paragraphStyles = {
    p: typographyStyle("body", `0 0 ${themeData["density.spacings.small"]} 0`),
    "h1 + p, h2 + p, h3 + p": typographyStyle("body", `${themeData["density.spacings.medium"]} 0 ${themeData["density.spacings.small"]} 0`)
  };
  const styles = {
    richText: _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, baseStyles), listStyles), headerStyles), anchorStyles), paragraphStyles), boldStyles)
  };
  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

const _excluded = ["children", "dangerouslySetInnerHTML"];

const RichText = props => /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, _ref => {
  let {
    resolvedRoles
  } = _ref;

  const {
    children,
    dangerouslySetInnerHTML
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded);

  const {
    className
  } = otherProps;
  const styles = stylesheet(props, resolvedRoles);
  return children ? /*#__PURE__*/React.createElement("div", {
    className: cx(css(styles.richText), className)
  }, children) : /*#__PURE__*/React.createElement("div", {
    className: cx(css(styles.richText), className),
    dangerouslySetInnerHTML: dangerouslySetInnerHTML // eslint-disable-line react/no-danger

  });
});

RichText.displayName = "RichText";
RichText.propTypes = {
  /**
   * React-generated markup to style with HIG typography rules
   */
  children: PropTypes.node,

  /**
   * HTML string to be rendered and styled with HIG typography rules
   */
  dangerouslySetInnerHTML: PropTypes.shape({
    __html: PropTypes.string
  }),

  /**
   * Adds custom/overriding styles
   */
  stylesheet: PropTypes.func
};
RichText.__docgenInfo = {
  "description": "",
  "displayName": "RichText",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "React-generated markup to style with HIG typography rules"
    },
    "dangerouslySetInnerHTML": {
      "type": {
        "name": "shape",
        "value": {
          "__html": {
            "name": "string",
            "required": false
          }
        }
      },
      "required": false,
      "description": "HTML string to be rendered and styled with HIG typography rules"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding styles"
    }
  }
};

export { RichText as default };
